import {initFlatPickerInput} from "../../../helpers/inputs/flatPicker/flatPicker";
import createElement from "../../../helpers/createElement";
import {initTomSelectInput} from "../../../helpers/inputs/tom-select/tom-select";
import {waitUntil} from "../../../helpers/waitUntil";
import _ from "lodash";
import {getInputsByName} from "../../../helpers/forms";
import {parseHtmlDate} from "../../../helpers/dates";
import {differenceInDays} from "date-fns";

/**
 * Initializes a VOE Script forms and behaviors
 *
 * @param {Element} scriptElement
 */
function initScriptForm(scriptElement) {
	/**
	 * Setup dates inputs
	 */
	scriptElement.querySelectorAll('.flat-picker-date').forEach(date => {
		initFlatPickerInput(date, {defaultDate: null});
	})

	/**
	 * Setup outcome selects
	 */
	scriptElement.querySelectorAll('select.voe-field-outcome').forEach(select => {
		initVoeFieldOutcomeSelect(select);
	})

	/**
	 * Setup outcome radio buttons
	 */
	scriptElement.querySelectorAll('input.voe-field-outcome').forEach(radio => {
		initVoeFieldOutcomeRadioButton(radio);
	})

	/**
	 * Setup inputs events
	 */
	const inputsWithSelect = [
		'employer_division',
		'job_title',
		'most_recent_start_date',
		'most_recent_end_date',
		'original_start_date',
		'years_with_employer',
		'months_with_employer',
		'reason_for_leaving',
	];

	// When changing and input we want to update its related outcome select
	inputsWithSelect.forEach(input => {
		const element = scriptElement.querySelector(
			`input[name="${input}"],textarea[name="${input}"]`
		);
		if (element) {
			// Initialize related outcome select
			const outcomeType = getRelatedOutcomeType(element);

			if (outcomeType === 'select') {
				const debouncedUpdate = _.debounce(() => {
					updateRelatedOutcomeSelect(element, input)
				}, 256, {trailing: true});

				element.addEventListener('change', debouncedUpdate);
				element.addEventListener('keyup', debouncedUpdate);

				// Initialize outcome selects with correct statuses
				// Must be run after component initialization
				setTimeout(() => {
					updateRelatedOutcomeSelect(element, input);
				}, 0)
			}

			// Initialize related outcome radio buttons
			if (outcomeType === 'radio') {
				const debouncedUpdate = _.debounce(() => {
					updateRelatedOutcomeRadioButtons(element, input)
				}, 256, {trailing: true});

				element.addEventListener('change', debouncedUpdate);
				element.addEventListener('keyup', debouncedUpdate);

				// Initialize outcome selects with correct statuses
				// Must be run after component initialization
				setTimeout(() => {
					updateRelatedOutcomeRadioButtons(element, input);
				}, 0)
			}
		}
	})

	/**
	 *  Set auto flags change events
	 *
	 *  We listen to the underlying input element events because
	 *  flatpickr does not seem to propagate events from it correctly
	 */

	if (getStartDateFlatpickrInstance()) {
		getStartDateFlatpickrInstance().element.oninput = handleDateVarianceFlagUpdate;
		getEndDateFlatpickrInstance().element.oninput = handleDateVarianceFlagUpdate;

		// Initialize flag
		setTimeout(() => {
			// Must be run after component initialization
			handleDateVarianceFlagUpdate();
		}, 0)
	}

	/**
	 * Methods
	 */

	/**
	 * @param {HTMLSelectElement} select
	 */
	function initVoeFieldOutcomeSelect(select) {
		// Init tom select
		const renderOption = (data) => {
			const option = data.$option;

			const color = option.dataset.color;
			const backgroundColor = option.dataset.backgroundColor;

			return createElement(`
				<div style="color: ${color}; background-color: ${backgroundColor}">
					${option.innerHTML}
				</div>
			`);
		}

		initTomSelectInput(
			select,
			{
				persist: false,
				create: false,
				render: {
					option: renderOption,
					item: renderOption
				}
			},
		);

		// Add events
		select.addEventListener('change', () => {
			const outcome = select.value;
			const targetName = select.dataset.verifiedTarget;
			if (outcome === 'verified') {
				// Updated related field
				updateRelatedOutcomeField(
					targetName,
					select.dataset.verifiedValue
				);
			} else if (outcome === 'unknown') {
				// Clear related field
				updateRelatedOutcomeField(
					targetName,
					''
				);
			} else {
				// Must be correction
				focusRelatedOutcomeFieldAndWaitForChange(targetName);
			}
		})
	}

	/**
	 * @param {HTMLInputElement} radioButton
	 */
	function initVoeFieldOutcomeRadioButton(radioButton) {
		// Add events
		radioButton.addEventListener('change', () => {
			const radioButtonContainer = $(radioButton).closest('.outcome-radio-buttons-container')[0];
			const outcome = radioButton.value;
			const targetName = radioButtonContainer.dataset.verifiedTarget;
			if (outcome === 'verified') {
				// Updated related field
				updateRelatedOutcomeField(
					targetName,
					radioButtonContainer.dataset.verifiedValue
				);
			} else if (outcome === 'unknown') {
				// Clear related field
				updateRelatedOutcomeField(
					targetName,
					''
				);
			} else {
				// Must be correction
				focusRelatedOutcomeFieldAndWaitForChange(targetName);
			}
		})
	}

	/**
	 * Retrieves target and updates it with new value, triggered when
	 * outcome select is changed
	 *
	 * @param target
	 * @param value
	 */
	function updateRelatedOutcomeField(target, value) {
		const specialFieldsCases = [
			'most_recent_start_date',
			'most_recent_end_date',
			'original_start_date',
			'total_time_with_employer',
		]

		if (specialFieldsCases.includes(target)) {
			if (
				target === 'most_recent_start_date' ||
				target === 'most_recent_end_date' ||
				target === 'original_start_date'
			) {
				// Set flatpickr dates
				/** @type {Object.<string,import('flatpickr/dist/types/instance').Instance>} */
				let flatpickrInstancesMap = {
					'most_recent_start_date': getStartDateFlatpickrInstance(),
					'most_recent_end_date': getEndDateFlatpickrInstance(),
					'original_start_date': getOriginalStartDateFlatpickrInstance(),
				};

				flatpickrInstancesMap[target].setDate(value)

				// Also trigger change event
				flatpickrInstancesMap[target].element.dispatchEvent(
					new Event('input', {
						bubbles: true,
					})
				);
			} else if (target === 'total_time_with_employer') {
				// Set years and months inputs
				/** @type {{years: int, months: int}} */
				if (value) {
					const yearsAndMonths = JSON.parse(value);

					getYearsWithEmployerInput().value = yearsAndMonths.years ? yearsAndMonths.years : '';
					getMonthsWithEmployerInput().value = yearsAndMonths.months ? yearsAndMonths.months : '';
				} else {
					// No value was passed

					getYearsWithEmployerInput().value = '';
					getMonthsWithEmployerInput().value = '';
				}
			}
		} else {
			// Default behavior, set verified input value on target
			const targetSelector = `input[name="${target}"],select[name="${target}"],textarea[name="${target}"]`;

			const targetElement = scriptElement.querySelector(targetSelector);

			switch (targetElement.tagName) {
				case 'INPUT':
				case 'SELECT':
				case 'TEXTAREA':
					targetElement.value = value;
					break;
				default:
					throw 'Invalid target element';
			}
		}
	}

	function focusRelatedOutcomeFieldAndWaitForChange(targetName) {
		// Handle special cases
		if (
			targetName === 'most_recent_start_date' ||
			targetName === 'most_recent_end_date' ||
			targetName === 'original_start_date'
		) {
			// Set flatpickr dates
			/** @type {Object.<string,import('flatpickr/dist/types/instance').Instance>} */
			let flatpickrInstancesMap = {
				'most_recent_start_date': getStartDateFlatpickrInstance(),
				'most_recent_end_date': getEndDateFlatpickrInstance(),
				'original_start_date': getOriginalStartDateFlatpickrInstance(),
			};

			flatpickrInstancesMap[targetName].open();

			// Wait until date picker is not longer open
			waitUntil(() => {
				return !flatpickrInstancesMap[targetName].isOpen
			}).then(() => {
				// We check again to update the related outcome, in case the value was not changed
				// this will force the outcome back to its proper value

				const outcomeType = getRelatedOutcomeType(flatpickrInstancesMap[targetName].element);
				if (outcomeType === 'select') {
					updateRelatedOutcomeSelect(flatpickrInstancesMap[targetName].element, targetName);
				} else if (outcomeType === 'radio') {
					updateRelatedOutcomeRadioButtons(flatpickrInstancesMap[targetName].element, targetName);
				}

			})
		} else {
			if (targetName === 'total_time_with_employer') {
				// Override target name
				targetName = 'years_with_employer';
			}

			const targetSelector = `input[name="${targetName}"],select[name="${targetName}"],textarea[name="${targetName}"]`;

			/**
			 * @type {HTMLInputElement}
			 */
			let element = scriptElement.querySelector(targetSelector);

			if (element) {
				// Focus
				element.focus();

				// Select text if possible
				if (element.setSelectionRange) {
					element.setSelectionRange(0, 10000);
				}

				// Wait until element is out of focus
				waitUntil(
					() => {
						return document.activeElement !== element
					},
					{
						msBetweenAttempts: 100,
					}
				).then(() => {
					// We check again to update the related select, in case the value was not changed
					// this will force the outcome select back to its proper value
					updateRelatedOutcomeSelect(element, targetName);
				})
			}
		}
	}

	function getCurrentValueFromYearsMonthsInputs() {
		const yearsElement = scriptElement
			.querySelector(`input[name="years_with_employer"]`);
		const monthsElement = scriptElement
			.querySelector(`input[name="months_with_employer"]`);

		if (yearsElement.value === '' && monthsElement.value === '') {
			// Both are empty, set current value to empty so it gets cleared
			return '';
		} else {
			// Get actual value
			let years = parseInt(yearsElement.value);
			if (isNaN(years)) {
				years = 0;
			}

			let months = parseInt(monthsElement.value);
			if (isNaN(months)) {
				months = 0;
			}

			// Recreate json to compare with verified value
			return JSON.stringify({
				years,
				months
			});
		}
	}

	/**
	 * Retrieves the target's related outcome select and updates by comparing
	 * the verified value with the current value
	 *
	 * @param element
	 * @param targetName
	 */
	function updateRelatedOutcomeSelect(element, targetName) {
		let currentValue = element.value;

		let relatedSelect;

		// Handle special cases
		if (targetName === 'years_with_employer' || targetName === 'months_with_employer') {
			// Get select
			relatedSelect = scriptElement
				.querySelector(
					`select.voe-field-outcome[data-verified-target="total_time_with_employer"]`
				)

			// Get proper current value
			currentValue = getCurrentValueFromYearsMonthsInputs();
		} else {
			// Just retrieve related select
			relatedSelect = scriptElement
				.querySelector(
					`select.voe-field-outcome[data-verified-target="${targetName}"]`
				)
		}

		if (!relatedSelect) {
			// Related select not found
			return;
		}

		// Compare and update select
		const verifiedValue = relatedSelect.dataset.verifiedValue;

		if (!currentValue) {
			// No value, clear select
			relatedSelect.value = 'unknown';
		} else {
			// Compare values
			if (currentValue.toLowerCase().trim() === verifiedValue.toLowerCase().trim()) {
				// Verified value
				relatedSelect.value = 'verified';
			} else {
				relatedSelect.value = 'correction';
			}
		}
		// Display select if hidden
		relatedSelect.tomselect.wrapper.classList.remove('opacity-0');

		// Set value on tom select
		relatedSelect.tomselect.setValue(relatedSelect.value, true);
	}

	/**
	 * Retrieves the target's related outcome select and updates by comparing
	 * the verified value with the current value
	 *
	 * @param element
	 * @param targetName
	 */
	function updateRelatedOutcomeRadioButtons(element, targetName) {
		let currentValue = element.value;

		let relatedButtonsContainer;

		// Handle special cases
		if (targetName === 'years_with_employer' || targetName === 'months_with_employer') {
			// Get select
			relatedButtonsContainer = scriptElement
				.querySelector(
					`.outcome-radio-buttons-container[data-verified-target="total_time_with_employer"]`
				)

			// Get proper current value
			currentValue = getCurrentValueFromYearsMonthsInputs();
		} else {
			// Just retrieve related select
			relatedButtonsContainer = scriptElement
				.querySelector(
					`.outcome-radio-buttons-container[data-verified-target="${targetName}"]`
				)
		}

		if (!relatedButtonsContainer) {
			// Related container not found
			return;
		}

		// Compare and update buttons
		const verifiedValue = relatedButtonsContainer.dataset.verifiedValue;

		if (!currentValue) {
			// No value, clear select
			relatedButtonsContainer.querySelector('input[value="unknown"]').checked = true;
		} else {
			// Compare values
			if (currentValue.toLowerCase().trim() === verifiedValue.toLowerCase().trim()) {
				// Verified value
				relatedButtonsContainer.querySelector('input[value="verified"]').checked = true;
			} else {
				relatedButtonsContainer.querySelector('input[value="correction"]').checked = true;
			}
		}
	}

	/**
	 * Returns the element type the related outcome display is using
	 * @param element
	 */
	function getRelatedOutcomeType(element) {
		// Initialize related outcome select
		const hasRelatedOutcomeSelect = !!$(element)
			.closest('.row')[0]
			.querySelector('select.voe-field-outcome');

		if (hasRelatedOutcomeSelect) {
			return 'select';
		}

		const hasRelatedOutcomeRadioButtons = !!$(element)
			.closest('.row')[0]
			.querySelector('.outcome-radio-buttons-container');

		if (hasRelatedOutcomeRadioButtons) {
			return 'radio';
		}

		return 'none';
	}

	function handleDateVarianceFlagUpdate() {
		const verifiedDateVarianceFlagContainer = scriptElement
			.querySelector('.verified-date-variance-flag-container');

		const flag = verifiedDateVarianceFlagContainer.querySelector('.verified-date-variance-flag-radio');

		const verifiedStartDate = getStartDateFlatpickrInstance().latestSelectedDateObj;
		const verifiedEndDate = getEndDateFlatpickrInstance().latestSelectedDateObj;

		const clientSuppliedDates = getInputsByName(scriptElement, [
			'client_supplied_most_recent_start_date',
			'client_supplied_most_recent_end_date'
		]);

		const clientStartDate = parseHtmlDate(clientSuppliedDates.client_supplied_most_recent_start_date);
		const clientEndDate = parseHtmlDate(clientSuppliedDates.client_supplied_most_recent_end_date);

		let daysDifference = 0;

		if (verifiedStartDate) {
			daysDifference += Math.abs(differenceInDays(verifiedStartDate, clientStartDate))
		}

		if (verifiedEndDate) {
			daysDifference += Math.abs(differenceInDays(verifiedEndDate, clientEndDate))
		}

		if (daysDifference > window['DAYS_DIFFERENCE_THRESHOLD_FOR_AUTO_FLAG']) {
			verifiedDateVarianceFlagContainer.classList.add('flag-active');
			flag.checked = true;
		} else {
			verifiedDateVarianceFlagContainer.classList.remove('flag-active');
			flag.checked = false;
		}
	}

	/**
	 * Get Inputs Methods
	 * TODO: Make sure webpack/babel is transpiling ?. otherwise this will blow up on old browsers
	 */

	/** @return {import('flatpickr/dist/types/instance').Instance} */
	function getStartDateFlatpickrInstance() {
		return scriptElement.querySelector('input[name="most_recent_start_date"]')?._flatpickr
	}

	/** @return {import('flatpickr/dist/types/instance').Instance} */
	function getEndDateFlatpickrInstance() {
		return scriptElement.querySelector('input[name="most_recent_end_date"]')?._flatpickr
	}

	/** @return {import('flatpickr/dist/types/instance').Instance} */
	function getOriginalStartDateFlatpickrInstance() {
		return scriptElement.querySelector('input[name="original_start_date"]')?._flatpickr
	}

	function getYearsWithEmployerInput() {
		return scriptElement.querySelector('input[name="years_with_employer"]')
	}

	function getMonthsWithEmployerInput() {
		return scriptElement.querySelector('input[name="months_with_employer"]')
	}

}


export {initScriptForm}
